import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../api/AuthProvider';
import { useCustomersByRestaurant, useUpdateCustomer } from '../../api/customers';
import { FaEdit } from 'react-icons/fa';

const CustomersListScreen = () => {
  const { profile } = useAuth(); // Get restaurantId from AuthProvider
  const { data: customers, isLoading, error } = useCustomersByRestaurant(profile?.restaurantid);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCustomerId, setEditingCustomerId] = useState(null); // Track customer being edited
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '' }); // Form state
  const { mutate: updateCustomer, isLoading: isUpdating } = useUpdateCustomer();
  const modalRef = useRef(null); // Ref for modal content
  const activeInputRef = useRef(null); // Ref for currently focused input
  const [modalHeight, setModalHeight] = useState('100%'); // Dynamic overlay height

  // Format date to DD-MM-YYYY
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'dd-MM-yyyy');
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Start editing a customer
  const startEditing = (customer) => {
    setEditingCustomerId(customer.id);
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Save updated customer details
  const handleSave = async (customerId) => {
    try {
      await updateCustomer(
        { customerId, ...editForm },
        {
          onSuccess: () => {
            setEditingCustomerId(null); // Close modal on success
            setEditForm({ name: '', phone: '', email: '' });
            activeInputRef.current = null; // Clear active input
          },
          onError: (error) => {
            console.error('Error updating customer:', error);
            alert('Failed to update customer: ' + error.message);
          },
        }
      );
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingCustomerId(null);
    setEditForm({ name: '', phone: '', email: '' });
    activeInputRef.current = null; // Clear active input
  };

  // Handle input focus to adjust modal position
  const handleInputFocus = (e) => {
    activeInputRef.current = e.target; // Track focused input
    setTimeout(() => {
      if (modalRef.current && activeInputRef.current) {
        const input = activeInputRef.current;
        const rect = input.getBoundingClientRect();
        const modalRect = modalRef.current.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        if (rect.bottom > viewportHeight - 100) {
          modalRef.current.scrollTo({
            top: input.offsetTop - modalRect.top - 20,
            behavior: 'smooth',
          });
        }
      }
    }, 300); // Delay to account for keyboard animation
  };

  // Handle keyboard events and viewport changes
  useEffect(() => {
    const handleVisualViewport = () => {
      if (window.visualViewport) {
        setModalHeight(`${window.visualViewport.height}px`);
      } else {
        setModalHeight(`${window.innerHeight}px`);
      }

      if (editingCustomerId && modalRef.current && window.visualViewport && activeInputRef.current) {
        const viewport = window.visualViewport;
        const viewportHeight = viewport.height;
        const inputRect = activeInputRef.current.getBoundingClientRect();
        const inputBottom = inputRect.bottom + viewport.offsetTop;

        // Adjust modal position to keep the focused input above the keyboard
        if (inputBottom > viewportHeight - 100) { // 100px buffer for keyboard
          modalRef.current.style.transform = `translateY(-${inputBottom - viewportHeight + 100}px)`;
        } else {
          modalRef.current.style.transform = 'translateY(0)'; // Reset when keyboard is not covering
        }
        modalRef.current.style.maxHeight = `${viewportHeight - 20}px`; // Fit within viewport
      } else if (modalRef.current) {
        modalRef.current.style.transform = 'translateY(0)'; // Center when no input is focused
        modalRef.current.style.maxHeight = `${window.visualViewport?.height - 20 || window.innerHeight - 20}px`; // Dynamic max height
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    if (editingCustomerId) {
      window.addEventListener('keydown', handleKeyDown);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleVisualViewport);
        window.visualViewport.addEventListener('scroll', handleVisualViewport);
      }
      window.addEventListener('resize', handleVisualViewport);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewport);
        window.visualViewport.removeEventListener('scroll', handleVisualViewport);
      }
      window.removeEventListener('resize', handleVisualViewport);
    };
  }, [editingCustomerId]);

  // Filter and sort customers
  const filteredAndSortedCustomers = customers
    ? [...customers]
        .filter((customer) => {
          const searchLower = searchTerm.toLowerCase();
          return (
            (customer.name?.toLowerCase() || '').includes(searchLower) ||
            (customer.phone?.toLowerCase() || '').includes(searchLower) ||
            (customer.email?.toLowerCase() || '').includes(searchLower)
          );
        })
        .sort((a, b) => {
          const order = sortOrder === 'asc' ? 1 : -1;
          if (sortField === 'reservations') {
            return (a.reservations - b.reservations) * order;
          }
          if (sortField === 'no_show_count') {
            return ((a.no_show_count || 0) - (b.no_show_count || 0)) * order;
          }
          if (sortField === 'created_at') {
            return (new Date(a.created_at) - new Date(b.created_at)) * order;
          }
          return 0;
        })
    : [];

  return (
    <div
      style={{
        padding: '20px',
        maxWidth: '1000px',
        minWidth: '85%',
        margin: '0 auto',
        backgroundColor: 'white',
        minHeight: '100vh',
      }}
    >
      <h2
        style={{
          fontSize: '24px',
          color: '#333',
          marginBottom: '20px',
          textAlign: 'center',
        }}
      >
        Листа на гости
      </h2>

      {customers?.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <input
            type="text"
            placeholder="Пребарајте по име,email, телефонски..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '10px',
              fontSize: '16px',
              width: '400px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              outline: 'none',
              transition: 'border-color 0.3s',
              minHeight: '44px', // Touch-friendly
            }}
            onFocus={(e) => (e.target.style.borderColor = '#3fb488')}
            onBlur={(e) => (e.target.style.borderColor = '#ccc')}
          />
        </div>
      )}

      {isLoading && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '16px',
            color: '#333',
          }}
        >
          Loading customers...
        </div>
      )}

      {error && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '16px',
            color: '#d32f2f',
          }}
        >
          Error: {error.message}
        </div>
      )}

      {!isLoading && !error && customers?.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '16px',
            color: '#333',
          }}
        >
          No customers found for this restaurant.
        </div>
      )}

      {customers?.length > 0 && (
        <div
          style={{
            backgroundColor: 'white',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '16px',
              color: '#333',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '2px solid #3fb488',
                }}
              >
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'black',
                  }}
                >
                  Име
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'black',
                  }}
                >
                  Телефон
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'black',
                  }}
                >
                  Емаил
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'black',
                  }}
                  onClick={() => handleSort('reservations')}
                >
                  Број на резервации
                  {sortField === 'reservations' && (
                    <span style={{ color: '#3fb488' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'black',
                  }}
                  onClick={() => handleSort('no_show_count')}
                >
                  Број на неостварени резервации
                  {sortField === 'no_show_count' && (
                    <span style={{ color: '#3fb488' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'black',
                  }}
                  onClick={() => handleSort('created_at')}
                >
                  Прва резервација
                  {sortField === 'created_at' && (
                    <span style={{ color: '#3fb488' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'black',
                  }}
                >
                  Измени
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: customer.no_show_count >= 2 ? '#ffcccc' : 'white', // Highlight high no-show customers
                  }}
                >
                  <td style={{ padding: '12px' }}>{customer.name}</td>
                  <td style={{ padding: '12px' }}>{customer.phone}</td>
                  <td style={{ padding: '12px' }}>{customer.email || 'N/A'}</td>
                  <td style={{ padding: '12px' }}>{customer.reservations}</td>
                  <td style={{ padding: '12px' }}>{customer.no_show_count || 0}</td>
                  <td style={{ padding: '12px' }}>{formatDate(customer.created_at)}</td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => startEditing(customer)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#3fb488',
                      }}
                      title="Edit customer"
                    >
                      <FaEdit size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {customers?.length > 0 && filteredAndSortedCustomers.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '16px',
            color: '#333',
            marginTop: '20px',
          }}
        >
          No customers match your search.
        </div>
      )}

      {editingCustomerId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: modalHeight, // Dynamic height
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Darker overlay like AddReservationModal
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '10px 0',
          }}
          onClick={handleCancel} // Close modal when clicking outside
        >
          <div
            ref={modalRef}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)', // Softer shadow
              transition: 'transform 0.3s ease-out', // Smooth transform like AddReservationModal
              overflowY: 'auto',
              maxHeight: `${window.visualViewport?.height - 20 || window.innerHeight - 20}px`, // Dynamic max height
              fontFamily: "'Inter', 'Arial', sans-serif", // Match AddReservationModal
              transform: 'translateY(0)', // Initial position
              animation: 'slideIn 0.3s ease-out', // Entry animation
            }}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <h3
              style={{
                marginBottom: '15px',
                color: '#333',
                fontSize: '20px',
                fontWeight: 500,
              }}
            >
              Промени детали на гостин
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <input
                type="text"
                name="name"
                value={editForm.name}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder="Customer Name"
                style={{
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px', // Match AddReservationModal
                  minHeight: '44px', // Touch-friendly
                  outline: 'none',
                  transition: 'border-color 0.3s, box-shadow 0.3s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3fb488';
                  e.target.style.boxShadow = '0 0 0 3px rgba(63, 180, 136, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccc';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              />
              <input
                type="tel"
                name="phone"
                value={editForm.phone}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder="Phone Number"
                style={{
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  minHeight: '44px',
                  outline: 'none',
                  transition: 'border-color 0.3s, box-shadow 0.3s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3fb488';
                  e.target.style.boxShadow = '0 0 0 3px rgba(63, 180, 136, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccc';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              />
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder="Email Address"
                style={{
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  minHeight: '44px',
                  outline: 'none',
                  transition: 'border-color 0.3s, box-shadow 0.3s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3fb488';
                  e.target.style.boxShadow = '0 0 0 3px rgba(63, 180, 136, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccc';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              />
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end',
                  marginTop: '12px',
                }}
              >
                <button
                  onClick={() => handleSave(editingCustomerId)}
                  disabled={isUpdating}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isUpdating ? '#a3a3a3' : '#3fb488', // Disabled state
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isUpdating ? 'not-allowed' : 'pointer',
                    minHeight: '44px', // Touch-friendly
                    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                  onMouseOver={(e) => {
                    if (!isUpdating) {
                      e.target.style.backgroundColor = '#2f8a66';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isUpdating) {
                      e.target.style.backgroundColor = '#3fb488';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {isUpdating ? 'Се зачувува...' : 'Зачувај'}
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    minHeight: '44px', // Touch-friendly
                    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#b91c1c';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#d32f2f';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  Откажи
                </button>
              </div>
            </div>
          </div>
          <style>{`
            @keyframes slideIn {
              from {
                transform: translateY(-20px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default CustomersListScreen;